import type { IpBanDto } from '@api/types/ip'

/** "203.0.113.0/24 · until 12 Oct" — what a ban chip or a row needs to say in one line. */
export function describeBanTerm(ban: IpBanDto): string {
  if (ban.liftedAt) return `lifted ${new Date(ban.liftedAt).toLocaleDateString()}`
  if (!ban.expiresAt) return 'until lifted'
  const expires = new Date(ban.expiresAt)
  return expires.getTime() < Date.now()
    ? `expired ${expires.toLocaleDateString()}`
    : `until ${expires.toLocaleDateString()}`
}

/** Mobile-safe, short, and the whole string is in the tooltip. */
export function shortUserAgent(ua: string): string {
  if (!ua) return ''
  // The product token is usually the useful part: "okhttp/4.12.0", "Expo/2.31 ...", a browser.
  if (/iPhone|iPad/i.test(ua)) return 'iOS'
  if (/Android/i.test(ua)) return 'Android'
  if (/okhttp|Expo|ReactNative/i.test(ua)) return 'Mobile app'
  if (/Edg\//i.test(ua)) return 'Edge'
  if (/Chrome\//i.test(ua)) return 'Chrome'
  if (/Firefox\//i.test(ua)) return 'Firefox'
  if (/Safari\//i.test(ua)) return 'Safari'
  return ua.length > 24 ? `${ua.slice(0, 24)}…` : ua
}

/** Shortest prefixes that still count as "a neighbourhood" rather than "a whole carrier". */
const BROAD_IPV4_PREFIX = 16
const BROAD_IPV6_PREFIX = 48

/**
 * How many addresses a CIDR rule covers, as words, when it is wide enough to be worth a second
 * look; null for a single address, a narrow range, or something that does not parse (the API
 * validates the rule; this only decides whether to warn).
 */
export function describeBroadRange(rule: string): string | null {
  const match = rule.trim().match(/^(.+)\/(\d{1,3})$/)
  if (!match) return null
  const prefix = Number(match[2])
  const isV6 = match[1].includes(':')
  if (isV6) {
    if (prefix > 128 || prefix >= BROAD_IPV6_PREFIX) return null
    // A /48 is typically one customer site, so count in those rather than in 2^80 addresses.
    return `/${prefix} covers ${(2 ** (BROAD_IPV6_PREFIX - prefix)).toLocaleString()} IPv6 site networks (/48s)`
  }
  if (prefix > 32 || prefix >= BROAD_IPV4_PREFIX) return null
  return `/${prefix} covers ${(2 ** (32 - prefix)).toLocaleString()} IPv4 addresses`
}
